import './paymentConfirmationModal.css';
import React, {Component} from "react";
import CurrencyInput from "react-currency-input-field";
import i18nService from "../../i18n/i18nService";

interface PaymentConfirmationModalProps {
    friendName: string,
    totalDebt: number,
    onConfirm: (amount: number, isFull: boolean) => void,
    onCancel: () => void,
}

interface PaymentConfirmationModalState {
    amount: string,
    isFull: boolean,
    error?: string,
}

class PaymentConfirmationModal extends Component<PaymentConfirmationModalProps, PaymentConfirmationModalState> {
    constructor(props: PaymentConfirmationModalProps) {
        super(props);
        this.state = {
            amount: (this.props.totalDebt / 100).toFixed(2),
            isFull: true,
        };
        this.handleConfirm = this.handleConfirm.bind(this);
        this.handleAmountChange = this.handleAmountChange.bind(this);
        this.handleFullPaymentToggle = this.handleFullPaymentToggle.bind(this);
    }

    handleFullPaymentToggle() {
        this.setState({
            isFull: !this.state.isFull,
            amount: this.state.isFull ? "" : (this.props.totalDebt / 100).toFixed(2),
            error: undefined,
        });
    }

    handleAmountChange(value: string | undefined) {
        if (!value) {
            this.setState({amount: "", error: undefined, isFull: false});
            return;
        }
        const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
        if (isNaN(numValue) || numValue <= 0) {
            this.setState({amount: value, error: i18nService.t('paymentConfirmation.amountInvalid'), isFull: false});
        } else if (numValue > this.props.totalDebt / 100) {
            this.setState({amount: value, error: i18nService.t('paymentConfirmation.amountExceedsDebt', 'Amount cannot exceed €{{maxAmount}}').replace('{{maxAmount}}', (this.props.totalDebt / 100).toFixed(2)), isFull: false});
        } else {
            const isFull = Math.abs(numValue - (this.props.totalDebt / 100)) < 0.01;
            this.setState({amount: value, error: undefined, isFull});
        }
    }

    handleConfirm() {
        if (this.state.isFull) {
            // Full payment
            this.props.onConfirm(this.props.totalDebt, true);
        } else {
            // Partial payment - need amount
            if (!this.state.amount || this.state.error) {
                this.setState({error: i18nService.t('paymentConfirmation.amountInvalid')});
                return;
            }
            const amount = parseFloat(this.state.amount.replace(/[^0-9.-]/g, ''));
            if (isNaN(amount) || amount <= 0) {
                this.setState({error: i18nService.t('paymentConfirmation.amountInvalid')});
                return;
            }
            this.props.onConfirm(Math.round(amount * 100), false); // Convert to cents
        }
    }

    render() {
        return (
            <div className="PaymentConfirmationModal-Backdrop" onClick={this.props.onCancel}>
                <div className="PaymentConfirmationModal-Container" onClick={(e) => e.stopPropagation()}>
                    <h2 className="PaymentConfirmationModal-Title">{i18nService.t('paymentConfirmation.title')}</h2>
                    <p className="PaymentConfirmationModal-Description">
                        {i18nService.t('paymentConfirmation.description', 'Did you pay <strong>{{friendName}}</strong> for the debt of €{{totalDebt}}?').replace('<strong>', '').replace('</strong>', '').replace('{{friendName}}', this.props.friendName).replace('{{totalDebt}}', (this.props.totalDebt / 100).toFixed(2))}
                    </p>
                    <div className="PaymentConfirmationModal-InputGroup">
                        <div className="PaymentConfirmationModal-FullPaymentToggle">
                            <label aria-label={i18nService.t('paymentConfirmation.paidInFull', 'Paid in Full')}>
                                <span className="PaymentConfirmationModal-ToggleSwitch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.isFull}
                                        onChange={this.handleFullPaymentToggle}
                                    />
                                    <span className="PaymentConfirmationModal-ToggleSlider"></span>
                                </span>
                                <span className="PaymentConfirmationModal-ToggleLabel">{i18nService.t('paymentConfirmation.paidInFull', 'Paid in Full')} (€{(this.props.totalDebt / 100).toFixed(2)})</span>
                            </label>
                        </div>
                        {!this.state.isFull && (
                            <>
                                <label className="PaymentConfirmationModal-Label">{i18nService.t('paymentConfirmation.amountPaidLabel', 'Amount Paid (€)')}</label>
                                <CurrencyInput
                                    className="PaymentConfirmationModal-Input"
                                    prefix="€"
                                    decimalsLimit={2}
                                    value={this.state.amount}
                                    onValueChange={this.handleAmountChange}
                                    placeholder="0.00"
                                    allowNegativeValue={false}
                                    autoFocus
                                />
                            </>
                        )}
                        {this.state.error && (
                            <div className="PaymentConfirmationModal-Error">{this.state.error}</div>
                        )}
                    </div>
                    <div className="PaymentConfirmationModal-Actions">
                        <button className="PaymentConfirmationModal-Button PaymentConfirmationModal-ButtonCancel" onClick={this.props.onCancel}>
                            {i18nService.t('common.cancel')}
                        </button>
                        <button 
                            className="PaymentConfirmationModal-Button PaymentConfirmationModal-ButtonConfirm" 
                            onClick={this.handleConfirm}
                            disabled={(!this.state.isFull && (!this.state.amount || !!this.state.error))}
                        >
                            {i18nService.t('paymentConfirmation.confirmPaymentButton', 'Confirm Payment')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default PaymentConfirmationModal;

